import React from 'react'
import { computed, get, observable, reaction, set, action } from 'mobx'
import { observer } from 'mobx-react'
import PortManagerContext from './contexts/PortManagerContext'
import when from './helpers/when'
import validate from './helpers/validate'
import { decodeOptionValue, getColumnCount, getWindowOptions } from './helpers/window-options'
import SettingsManagerContext from './contexts/SettingsManagerContext'
import { checkoutState } from './order/OrderForm'

class PortManager extends React.Component {
  @observable accessor settings = null
  @observable accessor operation = null

  @observable accessor standard_port_name = null

  ports = observable.array([])
  valid_steps = observable.object({
    'step_1': false,
    'step_2': false,
    'step_3': false,
    'step_4': false,
    'step_5': false,
  })

  constructor (props) {
    super(props)

    this.settings = props.settings
    this.operation = props.operation

    this.standard_port_name = this.settings.getOtherSettings('standard_port_name', 'Port')

    this.props.registerManager('PortManager',this)

    let existing_ports = this.fetchStorage()
    for (let i = 0; i < existing_ports.length; i++) {
      this.ports.push(existing_ports[i])
    }
    if (this.ports.length < 1) this.addPort()
    this._currentPort.set(this.ports[0])
    if (this.operation.get('current_port_id')) {
      this.changePort(this.operation.get('current_port_id'))
    }
    this.updateWindowOptions()
    reaction(() => ({
      model: this.getValue('model'),
      upper_height: this.getValue('upper_height')
    }), ({ model, upper_height }) => {
      if (model == 9000) {
        if (20 <= upper_height && upper_height < 50) {
          this.updateValue('rails_type', 'low')
        } else if (50 <= upper_height && upper_height < 70) {
          this.updateValue('rails_type', 'standard')
        } else if (70 <= upper_height && upper_height <= 300) {
          this.updateValue('rails_type', 'high')
        }
      } else if (model == 8000) {
        if (50 <= upper_height && upper_height < 70) {
          this.updateValue('rails_type', 'standard')
        } else if (70 <= upper_height && upper_height <= 300) {
          this.updateValue('rails_type', 'high')
        }
      }
    })
    reaction(() => ({
      model: this.getValue('model'),
      height: this.getPortHeight()
    }), ({ model, height }) => {
      if (model == 8000 || height < 212.6) {
        this.updateValue('door', 'none')
      }
    })
    reaction(() => {
      let upperHeight = Number(this.getValue('upper_height'))
      let portHeight = Number(this.getValue('height'))
      let totalHeight = upperHeight + portHeight
      const _settings = this.settings.getInputSettings('rent_lift')
      const liftRequiredHeight = _settings?.max_height ?? 500
      return totalHeight > liftRequiredHeight
    }, (requiresLift) => {
      if (!requiresLift) {
        this.updateValue('rent_lift', null)
      }
    })
  }

  _currentPort = observable.box({})

  get currentPort () { return this._currentPort.get() }

  @computed get hasMounting () {
    return !this.ports.some(port => port.installation !== 'yes')
  }

  @computed get vat () {
    const advanced_vat_flow_enabled = this.context.getStepSettings('step_5')
      .advanced_vat_flow_enabled
    if (!advanced_vat_flow_enabled) {
      return null
    }
    const isprivate = !checkoutState.is_company
    const older = checkoutState.is_building_old
    const pay_vat = checkoutState.should_pay_vat
    let vat
    if (!this.hasMounting) {
      vat = 21
    } else {
      if (isprivate) {
        if (older) {
          vat = 6
        } else {
          vat = 21
        }
      } else {
        if (pay_vat) {
          vat = 0
        } else {
          vat = 21
        }
      }
    }
    return vat
  }

  _portModalIsOpen = observable.box(false)

  get portModalIsOpen () {
    return this._portModalIsOpen.get()
  }

  _windowOptions = observable.box([])

  get windowOptions () {return this._windowOptions.get()}

  set windowOptions (val) {return this._windowOptions.set(val)}

  _doing_ajax = observable.box(false)

  get doing_ajax () { return this._doing_ajax.get() }

  @action
  changePort (port_id) {
    let port = this.getPort(port_id)
    if (typeof port === 'object') {
      this._currentPort.set(port)
      this.operation.store('current_port_id', port.port_id)
    }
  }

  getPort (port_id, return_index) {
    if (typeof return_index === 'undefined') return_index = false

    let ports = this.ports
    let ports_length = ports.length
    for (let i = 0; i < ports_length; i++) {
      if (ports[i].port_id === port_id) {
        return return_index ? i : ports[i]
      }
    }
    return false
  }

  @action
  addPort (switch_to_new_port) {
    let id = this.getNextPortId()
    const idInt = parseInt(id.substring(1))

    this.ports.push({
      port_id: id,
      name: this.standard_port_name + ' ' + (idInt + 1),
      height: '',
      width: '',
    })
    this.updateStorage()
    if (switch_to_new_port === true) {
      this.changePort(id)
    }
    return id
  }

  deletePort (port_id) {
    let index = this.getPort(port_id, true)
    let port = this.getPort(port_id)
    if (typeof index === 'number') {
      if (Object.keys(port).length <= 2) {
        this.hardDeletePort(port_id)
        return true
      } else {
        let message = this.context.getOtherSettings(
          'delete_port_confirmation',
          'This will permanently delete port "{port_name}"'
        )

        let bool = confirm(message.replace('{port_name}', port.name))
        if (bool) {
          this.hardDeletePort(port_id)
          return true
        }
      }
    }
    return false
  }

  @action
  hardDeletePort (port_id) {
    let index = this.getPort(port_id, true)
    if (typeof index === 'number') {
      this.ports.splice(index, 1)
    }
    this.updateStorage()
  }

  getNextPortId () {
    let id = 0
    let port_ids = this.ports.map((p) => p.port_id)
    while (port_ids.indexOf('_' + id) >= 0) {
      id++
    }
    return '_' + id
  }

  openPortModal () {
    this._portModalIsOpen.set(true)
  }

  @action
  closePortModal () {
    this._portModalIsOpen.set(false)
  }

  @action
  togglePortModal () {
    this._portModalIsOpen.set(!this.portModalIsOpen)
  }

  getValue (key) {
    return get(this.currentPort, key)
  }

  hasValue (key) {
    if (key === 'size') {
      let h = this.getValue('height')
      let w = this.getValue('width')
      return (typeof h !== 'undefined' && h !== '') && (typeof w !== 'undefined' && w !== '')
    }

    return typeof this.getValue(key) !== 'undefined'
  }

  @action
  updateValue (key, value) {
    set(this.currentPort, key, value)
    this.ports[this.getPort(this.currentPort.port_id, true)] = this.currentPort
    this.updateStorage()
    if (['height', 'width', 'model', 'color'].indexOf(key) >= 0) this.updateWindowOptions() // Update Window options if relevant fields are updated.
  }

  removeValue (key) {
    if (typeof this.currentPort[key] !== 'undefined') delete this.currentPort[key]
    this.updateStorage()
  }

  updateStorage () {
    this.operation.store('ports', JSON.stringify(this.ports))
  }

  fetchStorage () {
    let data = this.operation.get('ports')
    try {
      JSON.parse(data)
    } catch (e) {
      return []
    }
    data = JSON.parse(data)
    return typeof data === 'object' && data !== null ? data : []
  }

  finalizePorts () {
    this._doing_ajax.set(true)
    this.operation.save(this.finalizePortsCallback.bind(this))
  }

  finalizePortsCallback (res, status, xhr) {
    this._doing_ajax.set(false)
    if (res.success === true) {
      let new_ids = res.data.port_ids
      let idslen = new_ids.length
      for (let i = 0; i < idslen; i++) {
        this.ports[this.getPort(new_ids[i].old_id, true)].port_id = new_ids[i].new_id // Set port ID to the new ID, given by the server.
      }
      this.updateStorage()
    } else {
      console.log('Failed', res)
    }
  }

  when (condition, arg) {
    return when(this, this.currentPort, condition, arg)
  }

  isValid (name) {
    return validate(this, this.currentPort, name)
  }

  getValidModelColors (model) {
    const colors_for = {
      9000: [3000, 5010, 6005, 7016, 7021, 8017, 9002, 9005, 9006, 9007, 9010, 9004, 7015],
      8000: [7016, 9006],
    }
    if (typeof model === 'undefined') return colors_for
    return colors_for[model]
  }

  getPortWidth () {
    let width = parseInt(this.currentPort.width)

    if (isNaN(width) || width < 200) {
      width = 200
    } else if (width > 600) {
      width = 600
    }

    return width
  }

  getPortHeight () {
    let height = parseInt(this.currentPort.height)

    if (isNaN(height) || height < 200) {
      height = 200
    } else if (height > 500) {
      height = 500
    }

    return height
  }

  getPortImageSize () {
    const width = this.getPortWidth()
    const height = this.getPortHeight()
    if (width * 1.2 <= height) {
      return 'tall'
    }
    if (height * 1.3333 <= width) {
      return 'long'
    }
    if (height * 1.2 <= width) {
      return 'medium'
    }
    return 'normal'
  }

  getPortImageSrc (name) {
    return this.getPortImage([
      name,
      this.getImageFileNameByName(name) + '.png'
    ].join('/'))
  }

  getImageFileNameByName (name) {
    if (name === 'door' && this.getValue(name) !== 'none') {
      let prefix = this.getValue('door_direction') === 'left_out' ? '' : 'inverted_'
      return prefix + this.getValue(name)
    }
    if (name === 'rails_type' && this.getValue('roof_has_angle') === 'yes') {
      let prefix = 'angled_'
      return prefix + this.getValue(name)
    }
    return this.getValue(name)
  }

  getPortImage (src) {
    return [
      'port-images',
      src
    ].join('/')
  }

  updateWindowOptions () {
    this.windowOptions = getWindowOptions(this.currentPort.model, this.currentPort.width, this.currentPort.height)
    if (this.windowOptions.length === 1 && this.isValid('color')) {
      this.updateValue('windows', this.windowOptions[0])
    }
  }

  validateStep (step) {
    let fields = []
    if (step === 'step_1') {
      fields = [
        'model',
        'size',
        'color',
        'windows'
      ]
    } else if (step === 'step_2') {
      fields = [
        'door',
        'door_direction',
        'steps',
        'motor',
        // 'extra_products'
      ]
    } else if (step === 'step_3') {
      fields = [
        'upper_height',
        'rent_lift',
        'rails_type',
        'roof_has_angle',
        'roof_angle',
      ]
    } else if (step === 'step_4') {
      fields = [
        'delivery',
        'installation',
        'date',
        'uninstallation',
      ]
    }
    let fl = fields.length
    for (let i = 0; i < fl; i++) {
      if (validate(this, this.currentPort, fields[i]) !== true) {
        return false
      }
    }
    return true
  }

  getStepNames () {
    return [
      'step_1',
      'step_2',
      'step_3',
      'step_4',
      'step_5',
    ]
  }

  getStepValidations () {
    let steps = this.getStepNames()
    return steps.map((string, index) => this.validateStep(string))
  }

  getLastAccesibleStep () {
    let steps = this.getStepValidations()
    let step = 0
    for (let i = 0; i < steps.length; i++) {
      if (steps[i] !== true) break
      step = i
    }
    return step
  }

  getWindowProperties (door) {
    if (typeof this.getValue('windows') !== 'string' || this.getValue('windows') === 'no_windows') {
      return null
    }

    const value = this.getValue('windows')
    const windowOptions = getWindowOptions(this.getPortModel(), this.getPortWidth(), this.getPortHeight())
    let allowed = false
    for (let i = 0; i < windowOptions.length; i++) {
      if (windowOptions[i] === value) {
        allowed = true
      }
    }

    if (!allowed) {
      return null
    }

    if (typeof door !== 'boolean') {
      door = this.when('hasDoor')
    }

    getWindowOptions(this.getPortModel(), this.getPortWidth(), this.getPortHeight())

    const [type, rows] = decodeOptionValue(this.getValue('windows'))

    const columns = getColumnCount(type, parseInt(this.getValue('width')) * 10, door)

    return [type, rows, columns]
  }

  getDoorDirection () {
    const doorDirection = this.getValue('door_direction')
    if (typeof doorDirection !== 'undefined') {
      return doorDirection
    }

    return 'left_out'
  }

  getDoorSteps () {
    const doorSteps = this.getValue('steps')
    if (typeof doorSteps !== 'undefined') {
      return doorSteps
    }

    return 'standard'
  }

  getPortCSSHeightAndWidth () {
    let cssHeight, cssWidth

    let portHeight = this.getPortHeight()
    let portWidth = this.getPortWidth()

    portHeight = portHeight * 1.4

    if (portHeight > portWidth) {
      cssHeight = 60

      cssWidth = (portWidth / portHeight) * cssHeight
    } else {
      cssWidth = 60

      cssHeight = (portHeight / portWidth) * cssWidth
    }
    return [cssHeight, cssWidth]
  }

  getPortModel () {
    let model = this.getValue('model')
    if (typeof model === 'undefined') model = 9000

    return model
  }

  render () {
    return (
      <PortManagerContext.Provider value={this}>
        <React.Fragment>
          {this.props.children}
        </React.Fragment>
      </PortManagerContext.Provider>
    )
  }
}

PortManager.contextType = SettingsManagerContext

export default observer(PortManager)